import React, { useEffect, useState } from 'react';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, FormControlLabel, Grid, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { EmailsPreviewRecipient, EmailsPreviewResponse, previewReminderEmails, sendDashboardEmails } from '../rest/BackendApi';
import Loading from './Loading';
import { Link as RouterLink } from 'react-router';
import 'react-quill-new/dist/quill.snow.css';
import 'react-quill-new/dist/quill.bubble.css';
import Editor from './Editor/Editor';
import { EMAIL_TAGS } from '../model/EmailTags';
import Checkbox from '@mui/material/Checkbox';
import { plural } from '../util/StringUtil';

export type EmailSendingDialogProps = {
  open: boolean;
  onClose: Function;
  dashboardIds?: number[];
};

const BlurryDialog = styled(Dialog)<DialogProps>(({ theme }) => ({
  backdropFilter: 'blur(2px)',
}));

function summariseResults(results: Map<string, string>) {
  const success = [...results.values()].filter(value => value === 'SUCCESS').length;
  const failed = results.size - success;
  if (success > 0 && failed === 0) {
    return { success: `Sent ${plural(success, 'reminder email')}` };
  } else if (success > 0) {
    return { error: `Sent ${plural(success, 'reminder email')} but failed for ${plural(failed, 'organisation')}. Make sure they have a contact set.` };
  }
  return { error: 'Failed to send reminder emails, please check the contacts are set on those organisations' };
}

const EmailSendingDialog = (props: EmailSendingDialogProps) => {
  const { dashboardIds, open, onClose } = props;
  const [emailsPreview, setEmailsPreview] = useState<EmailsPreviewResponse | null>(null);
  const [bodyTemplate, setBodyTemplate] = useState<string | null>(null);
  const [attachTransactions, setAttachTransactions] = useState<boolean>(false);
  const [subjectTemplate, setSubjectTemplate] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (dashboardIds) {
      setLoading(true);
      previewReminderEmails(dashboardIds)
        .then(response => {
          setEmailsPreview(response);
          setBodyTemplate(response.body);
          setSubjectTemplate(response.subject);
          setLoading(false);
        })
        .catch(reason => {
          onClose({ error: "Couldn't generate email preview" });
          setLoading(false);
        });
    }
  }, [dashboardIds]);

  function sendEmails(dashboardIds: number[]) {
    if (bodyTemplate == null || bodyTemplate.length == 0) {
      onClose({ error: `Email content was empty. Skipped` });
      return;
    }
    if (subjectTemplate == null || subjectTemplate.length == 0) {
      onClose({ error: `Email subject was empty. Skipped` });
      return;
    }
    // Close straight away and let the page show progress, so sending doesn't block the screen
    const done = sendDashboardEmails(dashboardIds, subjectTemplate, bodyTemplate, attachTransactions)
      .then(summariseResults)
      .catch(error => ({ error: 'Failed to send reminder emails, ' + error }));
    onClose({ sending: `Sending ${plural(dashboardIds.length, 'reminder email')}`, done });
  }

  if (!open) {
    return;
  }

  if (!emailsPreview || loading) {
    return <Loading />;
  }

  function getRecipientWithMissingConfig(recipients: EmailsPreviewRecipient[]) {
    return recipients.filter(recipient => recipient.email == undefined || recipient.managerEmail == undefined);
  }

  function getValidRecipients(recipients: EmailsPreviewRecipient[]) {
    return recipients.filter(recipient => recipient.email != undefined && recipient.managerEmail != undefined);
  }

  const validCount = getValidRecipients(emailsPreview.to).length;
  const hasValidRecipients = validCount > 0;
  const labelSx = { color: 'text.secondary', fontSize: '0.875rem' };

  const nbMissingConfig = getRecipientWithMissingConfig(emailsPreview.to).length;
  const maxMissingToShow = 5;

  const ContactNotSetBanner = () => {
    if (!hasValidRecipients) {
      return (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          None of the selected organisations have both a contact and a client manager set, so there's no one to email. <br />
          Go to the{' '}
          <Link component={RouterLink} to="/organisations">
            Organisations screen
          </Link>{' '}
          to set them first.
        </Alert>
      );
    } else if (nbMissingConfig > 0) {
      return (
        <Alert severity="warning" style={{ marginBottom: '20px' }}>
          {plural(nbMissingConfig, 'organisation')} will be skipped because the contact or client manager isn't set:
          <br />
          {getRecipientWithMissingConfig(emailsPreview.to)
            .slice(0, maxMissingToShow)
            .map(org => (
              <span key={org.organisationName} style={{ fontStyle: 'italic', marginRight: 1 }}>
                {org.organisationName}
              </span>
            ))
            .reduce((accu: any[] | null, elem) => {
              return accu === null ? [elem] : [...accu, ', ', elem];
            }, null)}
          {nbMissingConfig > maxMissingToShow && (
            <span style={{ fontStyle: 'italic', marginRight: 1, marginLeft: 1 }}>... & {nbMissingConfig - maxMissingToShow} more</span>
          )}
          <br />
          Go to the{' '}
          <Link component={RouterLink} to="/organisations">
            Organisations screen
          </Link>{' '}
          to set them first.
        </Alert>
      );
    }
  };

  return (
    <BlurryDialog transitionDuration={0} hideBackdrop={true} open={open} onClose={() => onClose()} aria-labelledby="confirm-dialog">
      <DialogTitle id="confirm-dialog" sx={{ fontWeight: 600, pb: 1 }}>
        Send reminders
      </DialogTitle>

      {emailsPreview && (
        <DialogContent sx={{ pt: 1 }}>
          <ContactNotSetBanner />

          {hasValidRecipients && (
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={2} sx={labelSx}>
                From
              </Grid>
              <Grid item xs={10}>
                {emailsPreview.from}
              </Grid>
              <Grid item xs={2} sx={labelSx}>
                Reply to
              </Grid>
              <Grid item xs={10}>
                {emailsPreview.replyTo}
              </Grid>
              <Grid item xs={2} sx={labelSx}>
                To
              </Grid>
              <Grid item xs={10}>
                {getValidRecipients(emailsPreview.to).map(recipient => {
                  return (
                    <Chip key={recipient.dashboardId} sx={{ height: 24, mb: 1, mr: 1 }} variant="outlined" color="primary" label={recipient.organisationName} />
                  );
                })}
              </Grid>
              <Grid item xs={2} sx={labelSx}>
                Subject
              </Grid>
              <Grid item xs={10}>
                <Editor onContentUpdate={setSubjectTemplate} initialState={emailsPreview.subject} isHtml={false} tags={EMAIL_TAGS} />
              </Grid>
              <Grid item xs={12}>
                <Editor onContentUpdate={setBodyTemplate} initialState={emailsPreview.body} isHtml={true} showToolbar={true} tags={EMAIL_TAGS} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox onChange={e => setAttachTransactions(e.target.checked)} checked={attachTransactions} color="primary" />}
                  label="Attach unreconciled bank transactions as a CSV (when there are fewer than 100)"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">
          Cancel
        </Button>
        {hasValidRecipients && (
          <Button
            disabled={bodyTemplate == null || bodyTemplate.length == 0 || subjectTemplate == null || subjectTemplate.length == 0}
            variant="contained"
            onClick={() => dashboardIds && sendEmails(getValidRecipients(emailsPreview.to).map(recipient => recipient.dashboardId))}
          >
            Send {plural(validCount, 'email')}
          </Button>
        )}
      </DialogActions>
    </BlurryDialog>
  );
};
export default EmailSendingDialog;
